import { IconChevronLeft, IconChevronRight } from '@tvg/fuse-icons';
import { SrOnly, uuid } from '@tvg/fuse-utils';
import PropTypes from 'prop-types';
import React from 'react';
import { GridItem } from '../../fuse-responsive-grid/src/grid';
import Typography from '../../fuse-typography-element/src/typography';
import { columnGapRange } from './config';
import {
	ButtonContainer,
	CarouselButton,
	CarouselContainer,
	CarouselItemGrid,
	CarouselLayout,
	CarouselSnapSection,
	ChevronOverlayButton,
	OverflowContainer,
	Pip,
	PipContainer,
	SlideContentContainer,
	StyledGridContainer,
} from './styled-carousel';

const { useState, useRef, useEffect, useMemo, memo } = React;

const OverridesPropType = PropTypes.shape({
	slides: PropTypes.number.isRequired,
	gridItemSize: PropTypes.number.isRequired,
});

const propTypes = {
	xxsCardsPerPage: PropTypes.number,
	mCardsPerPage: PropTypes.number,
	lCardsPerPage: PropTypes.number,

	xxsOverrides: OverridesPropType,
	mOverrides: OverridesPropType,
	lOverrides: OverridesPropType,

	children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]).isRequired,
	chevronOverlay: PropTypes.bool,
	dataTestId: PropTypes.string.isRequired,
	gridAreaOverride: PropTypes.bool,
	gridContainerPadding: PropTypes.bool,
	responsiveTitle: PropTypes.bool,
	touchPeek: PropTypes.bool,
	title: PropTypes.string,
	columnGapSize: PropTypes.shape({
		xxs: PropTypes.oneOf(columnGapRange.xxs),
		m: PropTypes.oneOf(columnGapRange.m),
		l: PropTypes.oneOf(columnGapRange.l),
		xl: PropTypes.oneOf(columnGapRange.xl),
	}),

	componentId: PropTypes.string,
};

const defaultProps = {
	xxsCardsPerPage: 2,
	mCardsPerPage: 4,
	lCardsPerPage: 5,

	xxsOverrides: undefined,
	mOverrides: undefined,
	lOverrides: undefined,
	chevronOverlay: false,
	gridAreaOverride: false,
	gridContainerPadding: true,
	responsiveTitle: false,
	touchPeek: true,
	title: '',
	columnGapSize: undefined,
	componentId: uuid(),
};
/* istanbul ignore next */ /* jest cant emulate intersectionobserver */
const prevSlide = (slideContainer) => {
	slideContainer.scrollLeft -= slideContainer.offsetWidth;
};
/* istanbul ignore next */ /* jest cant emulate intersectionobserver */
const nextSlide = (slideContainer) => {
	slideContainer.scrollLeft += slideContainer.offsetWidth;
};
/* istanbul ignore next */ /* jest cant emulate intersectionobserver */
const pipSlide = (slideContainer, pipIndex, currentPage) => {
	if (currentPage === pipIndex) return;
	slideContainer.scrollLeft += slideContainer.offsetWidth * (pipIndex - currentPage);
};

function AccessibilityInstructions({ currentPage, totalSlides }) {
	return (
		<>
			<SrOnly>Use the right and left arrows to scroll through the image carousel</SrOnly>
			<SrOnly>
				Page {currentPage + 1} of <span className="SrOnlyXxs">{totalSlides.xxs}</span>
				<span className="SrOnlyM">{totalSlides.m}</span>
				<span className="SrOnlyL">{totalSlides.l}</span>
			</SrOnly>
		</>
	);
}

AccessibilityInstructions.propTypes = {
	currentPage: PropTypes.number.isRequired,
	totalSlides: PropTypes.object.isRequired,
};

AccessibilityInstructions.defaultProps = {};

/**
 *
 * @type {React.FC<import('../../fuse').CarouselProps>}
 */
export const Carousel = memo(
	({
		children,
		chevronOverlay,
		dataTestId,
		title,
		gridAreaOverride,
		gridContainerPadding,
		touchPeek,
		columnGapSize,
		responsiveTitle,
		xxsCardsPerPage,
		mCardsPerPage,
		lCardsPerPage,

		xxsOverrides,
		mOverrides,
		lOverrides,

		componentId,

		...props
	}) => {
		const [currentPage, setCurrentPage] = useState(0);
		const slideContainer = useRef();
		const timeoutList = useRef({});

		const totalSlides = useMemo(() => {
			const len = children.length;
			return {
				xxs: xxsOverrides?.slides ?? Math.ceil(len / xxsCardsPerPage),
				m: mOverrides?.slides ?? Math.ceil(len / mCardsPerPage),
				l: lOverrides?.slides ?? Math.ceil(len / lCardsPerPage),
			};
		}, [
			children.length,
			lCardsPerPage,
			lOverrides?.slides,
			mCardsPerPage,
			mOverrides?.slides,
			xxsCardsPerPage,
			xxsOverrides?.slides,
		]);

		const gridItemSizes = useMemo(
			() => ({
				xxs: xxsOverrides?.gridItemSize ?? Math.ceil(12 / xxsCardsPerPage),
				m: mOverrides?.gridItemSize ?? Math.ceil(12 / mCardsPerPage),
				l: lOverrides?.gridItemSize ?? Math.ceil(10 / lCardsPerPage),
			}),
			[
				lCardsPerPage,
				lOverrides?.gridItemSize,
				mCardsPerPage,
				mOverrides?.gridItemSize,
				xxsCardsPerPage,
				xxsOverrides?.gridItemSize,
			],
		);

		/* istanbul ignore next */ /* jest cant emulate intersectionobserver */
		const handleTabIntersection = (entries) => {
			entries.forEach((entry) => {
				// tabindex for buttons and links
				if (entry.isIntersecting) {
					entry.target.tabIndex = 0;
					entry.target.inert = false;
					// entry.target.dataset.ariaCurrent = 'location';
				} else {
					entry.target.tabIndex = -1;
					entry.target.inert = true;
					// entry.target.dataset.ariaCurrent = null;
				}
			});
		};

		/* istanbul ignore next */ /* jest cant emulate intersectionobserver */
		const handlePageIntersection = (entries) => {
			entries.forEach((entry) => {
				// set current page
				if (entry.isIntersecting) {
					timeoutList.current[entry.target.id] = setTimeout(() => {
						setCurrentPage(Number(entry.target.dataset.currentPage));
					}, 50);
				} else {
					clearTimeout(timeoutList.current[entry.target.id]);
				}
			});
		};

		useEffect(() => {
			const options = {
				root: slideContainer.current,
				rootMargin: '0px',
				threshold: 0.99,
			};

			const tabIndexIntersectionObserver = new IntersectionObserver(handleTabIntersection, options);
			const pageIntersectionObserver = new IntersectionObserver(handlePageIntersection, options);

			slideContainer.current
				.querySelectorAll(`.fuse-carousel__carousel-snap-section`)
				.forEach((target) => pageIntersectionObserver.observe(target));

			slideContainer.current
				.querySelectorAll(`.focusable > *`)
				.forEach((target) => tabIndexIntersectionObserver.observe(target));

			return () => {
				tabIndexIntersectionObserver.disconnect();
				pageIntersectionObserver.disconnect();
			};
		}, [handlePageIntersection, handleTabIntersection]);

		return (
			<CarouselContainer data-testid={dataTestId} {...props}>
				<StyledGridContainer className="container" gridContainerPadding={gridContainerPadding}>
					<CarouselLayout chevronOverlay={chevronOverlay} title={title} gridAreaOverride={gridAreaOverride}>
						<AccessibilityInstructions currentPage={currentPage} totalSlides={totalSlides} />
						{title && (
							<GridItem xxs={12} l={chevronOverlay ? 12 : 11} style={ {gridArea :'title'}} >
								<Typography type="heading" size={responsiveTitle ? { xxs: '3', m: '4', l: '5'} : 't2'} tag="h2">
									{title}
								</Typography>
							</GridItem>
						)}
						{/* gridAreaOverride ? {gridArea : 'revert-layer'} : */}
						<ButtonContainer  xxs={0} l={chevronOverlay ? 0 : 1} style={{ gridArea: 'prev' }}>
							<CarouselButton className={gridAreaOverride ? "fuse-carousel__button-previous" : ""}

								/* jest cant emulate intersectionobserver */
								onClick={/* istanbul ignore next */ () => prevSlide(slideContainer.current)}
								icon={<IconChevronLeft />}
								data-testid={`${dataTestId}-previous`}
								disabled={currentPage === 0}
							>
								<SrOnly>Previous slide</SrOnly>
							</CarouselButton>
						</ButtonContainer>
						<GridItem
							xxs={12}
							l={chevronOverlay ? 12 : 10}
							style={{ gridArea: 'slides', position: 'relative' }}
						>
							<OverflowContainer
								id={`${dataTestId}`}
								chevronOverlay={chevronOverlay}
								touchPeek={touchPeek}
								ref={slideContainer}
							>
								{chevronOverlay && (
									<ChevronOverlayButton
										/* jest cant emulate intersectionobserver */
										onClick={/* istanbul ignore next */ () => prevSlide(slideContainer.current)}
										icon={<IconChevronLeft />}
										data-testid={`${dataTestId}-previous`}
										disabled={currentPage === 0}
										position="left"
									>
										<SrOnly>Previous slide</SrOnly>
									</ChevronOverlayButton>
								)}
								<SlideContentContainer
									xxsSlides={totalSlides.xxs}
									mSlides={totalSlides.m}
									lSlides={totalSlides.l}
									chevronOverlay={chevronOverlay}
									touchPeek={touchPeek}
									columnGapSize={columnGapSize}
								>
									<CarouselItemGrid
										xxsSlides={totalSlides.xxs}
										mSlides={totalSlides.m}
										lSlides={totalSlides.l}
										chevronOverlay={chevronOverlay}
										role="list"
										columnGapSize={columnGapSize}
									>
										{[...Array(totalSlides.xxs).keys()].map((item, index) => (
											<CarouselSnapSection
												key={item}
												id={`slide-${index}-${componentId}`}
												chevronOverlay={chevronOverlay}
												data-current-page={index}
												role="listitem"
											/>
										))}
										{React.Children.map(children, (child) => (
											<GridItem
												xxs={gridItemSizes.xxs}
												m={chevronOverlay ? 12 : gridItemSizes.m}
												l={chevronOverlay ? 12 : gridItemSizes.l}
												className="grid-item focusable fuse-carousel__grid-item-children"
												role="listitem"
											>
												{child}
											</GridItem>
										))}
									</CarouselItemGrid>
								</SlideContentContainer>
								{chevronOverlay && (
									<ChevronOverlayButton
										/* jest cant emulate intersectionobserver */
										onClick={/* istanbul ignore next */ () => nextSlide(slideContainer.current)}
										icon={<IconChevronRight />}
										data-testid={`${dataTestId}-next`}
										disabled={currentPage === totalSlides.l - 1}
										position="right"
									>
										<SrOnly>Next slide</SrOnly>
									</ChevronOverlayButton>
								)}
							</OverflowContainer>
						</GridItem>
						<ButtonContainer xxs={0} l={chevronOverlay ? 0 : 1} style={{ gridArea: 'next' }}>
							<CarouselButton className={gridAreaOverride ? "fuse-carousel__button-next" : ""}
								/* jest cant emulate intersectionobserver */
								onClick={/* istanbul ignore next */ () => nextSlide(slideContainer.current)}
								icon={<IconChevronRight />}
								data-testid={`${dataTestId}-next`}
								disabled={currentPage === totalSlides.l - 1}
							>
								<SrOnly>Next slide</SrOnly>
							</CarouselButton>
						</ButtonContainer>
						<PipContainer
							mSlides={totalSlides.m}
							lSlides={totalSlides.l}
							style={{ gridArea: 'pips' }}
						>
							{[...Array(totalSlides.xxs).keys()].map((item, index) => (
								<Pip
									key={item}
									role="button"
									className={currentPage === index && `current`}
									data-testid={`${dataTestId}-slide-${index}`}
									aria-current={currentPage === index}
									/* jest cant emulate intersectionobserver */
									onClick={/* istanbul ignore next */() => pipSlide(slideContainer.current, index, currentPage)}
								>
									<SrOnly>Go to page {index + 1}</SrOnly>
								</Pip>
							))}
						</PipContainer>
					</CarouselLayout>
				</StyledGridContainer>
			</CarouselContainer>
		);
	},
	/* istanbul ignore next */ /* jest cant emulate intersectionobserver */
	(prevProps, newProps) => prevProps === newProps,
);

Carousel.displayName = 'FuseCarousel';
Carousel.propTypes = propTypes;
Carousel.defaultProps = defaultProps;

export default Carousel;
